import { formatDate, roundTo } from "../utils/general_util";
import RequestChangesModal from "./RequestChangesModal";
import React, { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";

function prettyTicketType(type) {
    if (!type) return "N/A";
    return type === "reactive" ? "Reactive" : "Proactive";
}

function prettyStatus(status) {
    if (!status) return "Pending";
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function TicketDetailsDialog({
    ticket,
    onClose,
    onMarkAsFinished,
    isFinished,
    onMarkAsDone,
    inlineNotesEditable = false,
    currentUserEmail = "",
}) {

    const [requestChangesOpen, setRequestChangesOpen] = useState(false);
    const [notesDraft, setNotesDraft] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);

    const {
        ticketId,
        title,
        ticketTitle,
        ticketType,
        status,
        createdAt,
        createdBy,
        reviewedBy,
        updatedAt,
        description,
        rootCause,
        actionsTaken,
        notes,
        improvementPlan,
        finalComments,
        currency,
        labourHours,
        initialInvestment,
        annualMaintenance,
        prodActive,
        prodFrequency,
        prodFrequencyUnit,
        prodHoursSaved,
        directActive,
        directBenefits,
        intActive,
        intangibleLevel,
        financialRoi,
        totalInitialInvestmentCost,
        netAnnualBenefit,
        benefitCostRatio,
        breakevenPoint,
        implementationStatus,
        daysRequired,
    } = ticket || {};

    const email =
        currentUserEmail ||
        localStorage.getItem("userEmail") ||
        localStorage.getItem("email");
    const creatorEmail = createdBy || ticket?.created_by || "";
    const reviewerEmail = reviewedBy || ticket?.reviewed_by || "";
    const normalizedUserEmail = String(email || "").trim().toLowerCase();
    const normalizedCreatorEmail = String(creatorEmail || "").trim().toLowerCase();
    const normalizedReviewerEmail = String(reviewerEmail || "").trim().toLowerCase();
    const isReviewer =
        Boolean(normalizedUserEmail) && normalizedReviewerEmail === normalizedUserEmail;
    const isCreator =
        Boolean(normalizedUserEmail) && normalizedCreatorEmail === normalizedUserEmail;
    const isDone =
        implementationStatus != null &&
        String(implementationStatus).trim().toLowerCase() === "done";
    const initialNotes = notes || "";
    const canInlineEditNotes = inlineNotesEditable && isCreator && !isDone;
    const notesChanged = notesDraft !== initialNotes;

    const showProductivity =
        prodActive && (prodFrequency || prodHoursSaved);
    const showDirectBenefits =
        directActive && Array.isArray(directBenefits) && directBenefits.length > 0;
    const showIntangible = intActive && intangibleLevel;
    const hasCostFields =
        currency || labourHours || initialInvestment || annualMaintenance;

    const hasFinancialAnalysis =
        totalInitialInvestmentCost != null ||
        financialRoi != null ||
        netAnnualBenefit != null ||
        benefitCostRatio != null ||
        breakevenPoint != null;

    function statusBadgeClasses(status) {
        const s = String(status || "pending").toLowerCase();

        switch (s) {
            case "completed":
                return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100";
            case "abandoned":
                return "bg-red-50 text-red-700 ring-1 ring-red-100";
            case "pending":
            default:
                return "bg-blue-50 text-blue-700 ring-1 ring-blue-100";
        }
    }

    useEffect(() => {
        setNotesDraft(initialNotes);
    }, [initialNotes, ticketId]);

    async function handleUpdateNotes() {
        if (!canInlineEditNotes || !notesChanged) return;
        try {
            setSavingNotes(true);
            await axiosInstance.post("/ticket/save-step", {
                ticketId,
                data: {
                    notes: notesDraft,
                },
            });
            ticket.notes = notesDraft;
            toast.success("Notes updated successfully.");
        } catch (err) {
            toast.error("Failed to update notes. Please try again.");
        } finally {
            setSavingNotes(false);
        }
    }

    if (!ticket) return null;

    return (
        <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-slate-200 pb-3">
                    <div className="text-left">
                        <p className="text-xs font-medium text-slate-500">
                            Ticket ID:{" "}
                            <span className="text-slate-900">{ticketId ?? "N/A"}</span>
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {title || ticketTitle || "(No title)"}
                        </h2>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-600">
                            <span
                                className={`inline-flex items-center rounded-full px-2.5 py-1 font-medium ring-1 shadow-sm ${statusBadgeClasses(status)}`}
                            >
                                {prettyStatus(status)}
                            </span>
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700 ring-1 ring-slate-200 shadow-sm">
                                {prettyTicketType(ticketType)}
                            </span>
                            {createdBy && (
                                <span className="inline-flex items-center rounded-full bg-lime-100 px-2.5 py-1 font-medium text-slate-700 ring-1 ring-lime-400 shadow-sm">
                                    Created by: {createdBy}
                                </span>
                            )}
                            {reviewedBy && (
                                <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 font-medium text-slate-700 ring-1 ring-violet-400 shadow-sm">
                                    Reviewed by: {reviewedBy}
                                </span>
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                        aria-label="Close ticket details"
                    >
                        ❌
                    </button>
                </div>

                {/* Body */}
                <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-1 text-sm text-slate-800 text-left">
                    {/* Description */}
                    {description && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Description
                            </h3>
                            <p className="whitespace-pre-line text-sm text-slate-800">
                                {description}
                            </p>
                        </section>
                    )}

                    {/* Root cause (5-Why) */}
                    {rootCause && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Root cause analysis (5-Why)
                            </h3>
                            <p className="whitespace-pre-line text-sm text-slate-800">
                                {rootCause}
                            </p>
                        </section>
                    )}

                    {/* Actions taken */}
                    {actionsTaken && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Actions taken
                            </h3>
                            <p className="whitespace-pre-line text-sm text-slate-800">
                                {actionsTaken}
                            </p>
                        </section>
                    )}

                    {/* Cost block */}
                    {hasCostFields && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Improvement project · Cost
                            </h3>
                            <div className="grid gap-2 text-xs sm:grid-cols-2">
                                {currency && (
                                    <div>
                                        <span className="font-semibold text-slate-700">
                                            Currency:
                                        </span>{" "}
                                        {currency}
                                    </div>
                                )}
                                {labourHours && (
                                    <div>
                                        <span className="font-semibold text-slate-700">
                                            Labour hours to implement:
                                        </span>{" "}
                                        {labourHours}
                                    </div>
                                )}
                                {initialInvestment && (
                                    <div>
                                        <span className="font-semibold text-slate-700">
                                            Initial investment:
                                        </span>{" "}
                                        {initialInvestment}
                                        {currency && ` ${currency}`}
                                    </div>
                                )}
                                {annualMaintenance && (
                                    <div>
                                        <span className="font-semibold text-slate-700">
                                            Annual maintenance:
                                        </span>{" "}
                                        {annualMaintenance}
                                        {currency && ` ${currency}`}
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Benefits block */}
                    {(showProductivity || showDirectBenefits || showIntangible || financialRoi) && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Improvement project · Benefits
                            </h3>

                            <div className="space-y-3 text-xs">
                                {showProductivity && (
                                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                        <div className="font-semibold text-slate-800">
                                            Productivity
                                        </div>
                                        <div>
                                            Frequency:{" "}
                                            {prodFrequency && prodFrequencyUnit
                                                ? `${prodFrequency} ${Number(prodFrequency) === 1 ? "time" : "times"} per ${prodFrequencyUnit}`
                                                : "-"}
                                        </div>
                                        <div>
                                            Hours saved per occurrence:{" "}
                                            {prodHoursSaved || "-"}
                                        </div>
                                    </div>
                                )}

                                {showDirectBenefits && (
                                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                        <div className="mb-1 font-semibold text-slate-800">
                                            Direct financial
                                        </div>
                                        <div className="space-y-1">
                                            {directBenefits.map((row, idx) => (
                                                <div
                                                    key={row.id || idx}
                                                    className="flex flex-wrap gap-2"
                                                >
                                                    {row.description && (
                                                        <span className="font-medium">
                                                            {row.description}
                                                        </span>
                                                    )}
                                                    {row.amount && (
                                                        <span>
                                                            — {row.amount}
                                                            {currency && ` ${currency}`}
                                                        </span>
                                                    )}
                                                    {row.frequency && (
                                                        <span className="text-slate-500">
                                                            ({row.frequency})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {showIntangible && (
                                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                                        <div className="font-semibold text-slate-800">
                                            Intangible / indirect
                                        </div>
                                        <div>Level: {intangibleLevel}</div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Financial analysis */}
                    {hasFinancialAnalysis && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Financial analysis
                            </h3>
                            <div className="grid gap-2 text-xs sm:grid-cols-2">
                                {totalInitialInvestmentCost != null && (
                                    <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
                                        <p className="text-sm text-orange-900">
                                            Total Initial Investment Cost:{" "}
                                            <span className="font-semibold">
                                                {totalInitialInvestmentCost} {currency}
                                            </span>
                                        </p>
                                    </div>
                                )}
                                {/* {financialRoi && (
                                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                                        <p className="text-sm text-emerald-900">
                                            ROI:{" "}
                                            <span className="font-semibold">
                                                {financialRoi} {currency}
                                            </span>
                                        </p>
                                    </div>
                                )} */}
                                {netAnnualBenefit != null && (
                                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                                        <p className="text-sm text-emerald-900">
                                            Net Annual Benefit:{" "}
                                            <span className="font-semibold">
                                                {netAnnualBenefit} {currency}
                                            </span>
                                        </p>
                                    </div>
                                )}
                                {benefitCostRatio != null && (
                                    <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2">
                                        <p className="text-sm text-purple-900">
                                            Benefit Cost Ratio:{" "}
                                            <span className="font-semibold">
                                                {benefitCostRatio ? roundTo(Number(benefitCostRatio), 1) : "N/A"}
                                            </span>
                                        </p>
                                    </div>
                                )}
                                {breakevenPoint != null && (
                                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                                        <p className="text-sm text-blue-900">
                                            Breakeven (years):{" "}
                                            <span className="font-semibold">
                                                {breakevenPoint}
                                            </span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Notes */}
                    {(notes || inlineNotesEditable) && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Notes
                            </h3>
                            <textarea
                                className="min-h-[140px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300 disabled:bg-slate-100 disabled:text-slate-600"
                                value={notesDraft}
                                onChange={(e) => setNotesDraft(e.target.value)}
                                disabled={!canInlineEditNotes || savingNotes}
                                placeholder="No notes added."
                            />
                            {canInlineEditNotes && (
                                <div className="mt-3 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={handleUpdateNotes}
                                        disabled={!notesChanged || savingNotes}
                                        className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {savingNotes ? "Updating..." : "Update Notes"}
                                    </button>
                                </div>
                            )}
                        </section>
                    )}

                    {improvementPlan && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Improvement Plan
                            </h3>
                            <p className="whitespace-pre-line text-sm text-slate-800">
                                {improvementPlan}
                            </p>
                        </section>
                    )}

                    {finalComments && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Final Comments
                            </h3>
                            <p className="whitespace-pre-line text-sm text-slate-800">
                                {finalComments}
                            </p>
                        </section>
                    )}

                    {/* Timestamps moved to bottom */}
                    {(createdAt || updatedAt) && (
                        <section className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Time Information
                            </h3>
                            <div className="space-y-1">
                                {daysRequired && (
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 w-1/2 mb-2">
                                        <p className="text-red-900 text-sm">
                                            Days required to implement:{" "}
                                            <span className="font-semibold">
                                                {daysRequired + " day" + (Number(daysRequired) === 1 ? "" : "s")}
                                            </span>
                                        </p>
                                    </div>
                                )}
                                {createdAt && (
                                    <div className={`${daysRequired ? "px-3 " : ""}text-xs`}>
                                        <span className="font-semibold text-slate-700">
                                            Created:
                                        </span>{" "}
                                        {formatDate(createdAt)}
                                    </div>
                                )}
                                {updatedAt && (
                                    <div className={`${daysRequired ? "px-3 " : ""}text-xs`}>
                                        <span className="font-semibold text-slate-700">
                                            Last updated:
                                        </span>{" "}
                                        {formatDate(updatedAt)}
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-4 flex justify-end border-t border-slate-200 pt-3">
                    {!isFinished && isReviewer && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setRequestChangesOpen(true)}
                                className="mr-3 inline-flex items-center rounded-full bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-yellow-600"
                            >
                                Request Changes
                            </button>
                            {/* buttons to complete as completed or abandoned */}
                            <button
                                type="button"
                                onClick={() => onMarkAsFinished(ticketId, "completed")}
                                className="mr-3 inline-flex items-center rounded-full bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-green-700"
                            >
                                Mark as Completed
                            </button>

                            <button
                                type="button"
                                onClick={() => onMarkAsFinished(ticketId, "abandoned")}
                                className="mr-3 inline-flex items-center rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700"
                            >
                                Mark as Abandoned
                            </button>
                        </div>
                    )}
                    {isCreator && !isDone && isFinished && (
                        <button
                            type="button"
                            onClick={() => onMarkAsDone(ticketId, "done")
                            }
                            className="mr-3 inline-flex items-center rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                        >
                            Mark as Done
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-black"
                    >
                        Close
                    </button>
                </div>

                <RequestChangesModal
                    open={requestChangesOpen}
                    onClose={() => setRequestChangesOpen(false)}
                    ticketId={ticket.ticketId}
                    onSubmitted={() => {
                        // close dialog
                        setRequestChangesOpen(false);
                        // refresh ticket details
                        onClose();
                    }}
                />
            </div>
        </div>
    );
}

export default TicketDetailsDialog;
